import React, {createContext, PropsWithChildren, useMemo} from 'react';
import {Account} from '../models';
import {
  AccountService,
  AccountServiceImpl,
  DownloadInviteParams,
  DownloadInviteService,
  DownloadInviteServiceImpl,
  EmailRegistrationParams,
  EmailVerificationParams,
  ResendVerificationParams,
} from '../services';
import {ServiceResponse} from '../services/api';
import {DownloadInviteResponse} from '../services/download-invite/download-invite-params';

export interface AccountRegistrationProviderInterface {
  accountService: AccountService;
  downloadInviteService: DownloadInviteService;
  registerByEmail: (params: {firstName: string; lastName?: string; email: string; status?: string}) => Promise<ServiceResponse<Account>>;
  resendVerificationEmail: (accountId: string) => Promise<ServiceResponse<boolean>>;
  sendDownloadInvite: (params: DownloadInviteParams) => Promise<ServiceResponse<DownloadInviteResponse>>;
  verifyEmail: (accountId: string, token: string) => Promise<ServiceResponse<boolean>>;
  verifyEmailOtp: (accountId: string, token: string) => Promise<ServiceResponse<{token: string}>>;
}

export const AccountRegistrationContext = createContext<AccountRegistrationProviderInterface>(
  undefined as any as AccountRegistrationProviderInterface,
);

export const AccountRegistrationProvider: React.FC<PropsWithChildren<{}>> = ({children}) => {
  const accountService: AccountService = useMemo(() => new AccountServiceImpl(), []);
  const downloadInviteService: DownloadInviteService = useMemo(() => new DownloadInviteServiceImpl(), []);

  const registerByEmail = async (params: {
    firstName: string;
    lastName?: string;
    email: string;
    status?: string;
  }): Promise<ServiceResponse<Account>> => {
    const registrationParams = new EmailRegistrationParams(params);
    return accountService.registerByEmail(registrationParams);
  };

  const verifyEmail = async (accountId: string, token: string): Promise<ServiceResponse<boolean>> => {
    const verificationParams = new EmailVerificationParams(accountId, token);
    return accountService.verifyEmail(verificationParams);
  };

  const resendVerificationEmail = async (accountId: string): Promise<ServiceResponse<boolean>> => {
    const resendParams = new ResendVerificationParams(accountId);
    return accountService.resendVerificationEmail(resendParams);
  };

  const sendDownloadInvite = async (params: DownloadInviteParams): Promise<ServiceResponse<DownloadInviteResponse>> => {
    return downloadInviteService.sendDownloadInvite(params);
  };

  const verifyEmailOtp = async (accountId: string, token: string): Promise<ServiceResponse<{token: string}>> => {
    return accountService.verifyEmailOtp(accountId, token);
  };

  return (
    <AccountRegistrationContext.Provider
      value={{
        accountService,
        downloadInviteService,
        registerByEmail,
        resendVerificationEmail,
        sendDownloadInvite,
        verifyEmail,
        verifyEmailOtp,
      }}>
      {children}
    </AccountRegistrationContext.Provider>
  );
};
